/**
 * The main namespace of the TomTom Maps JavaScript SDK. This is a place where you get access to all the services,
 * Leaflet main object (L) and configuration.
 *
 * @class tomtom
 * @module The tomtom namespace
 */
define('main',['require','polyfills','pancontrol','zoomslider','requester/requester','display/map','services/trafficIncidents','services/copyright','services/copyrightCaption','services/geocode','services/structuredGeocode','services/reverseGeocode/reverseGeocode','services/reverseGeocode/crossStreetLookup','services/search/fuzzySearch','services/search/poiSearch','services/search/categorySearch','services/search/geometrySearch','services/search/nearbySearch','services/search/lowBandwidthSearch','services/routing','widgets/controls/searchBox','widgets/controls/location','widgets/controls/routeInputs','widgets/controls/routeInstructions','unitFormatConverter/unitFormatConverter','widgets/views/markers','widgets/views/routeOnMap','widgets/views/routeSummary','widgets/views/miniMap','security','mapboxProvider'],function(require) {
    'use strict';
    var tomtom, requester, L, security, mapboxProvider;

    require('polyfills');

    // Bundled leaflet plugins
    require('pancontrol');
    require('zoomslider');

    tomtom = {};

    requester = require('requester/requester');
    /**
     * Makes the SDK to use JSONP calls instead of JSON.
     *
     * @example
     * ```javascript
     *
     * tomtom.useJsonp(true); // all requests will be JSONP now
     * ```
     *
     * @method useJsonp
     * @param {Boolean} [useJsonp] Should the SDK use JSONP calls instead of default JSON.
     */
    tomtom.useJsonp = requester.useJsonp;
    /**
     * Makes the SDK to use HTTP calls instead of HTTPS. This can be modified on per service call basis with the
     * _protocol_ functions.
     *
     * @example
     * ```javascript
     *
     * tomtom.useHttp(true); // all requests will be HTTP now
     * ````
     *
     * @method useHttp
     * @param {Boolean} [useHttp] Should the SDK use HTTP calls instead of default HTTPS.
     */
    tomtom.useHttp = requester.useHttp;

    L = require('display/map');
    /**
     * <a href="../classes/L.html">The Leaflet object...</a>
     *
     * @property L
     * @type {Object}
     */
    tomtom.L = L;
    /**
     * <a href="../classes/L.html">The Leaflet object...</a>
     *
     * @property display
     * @type {Object}
     */
    tomtom.display = L;

    //just to be backward compatible (we build car with thill)
    tomtom.Map = L.Map;
    /**
     * <a href="../classes/L.html">The Leaflet map constructor...</a>
     *
     * @method map
     */
    tomtom.map = L.map;

    /**
     * <a href="../classes/trafficIncidents.html">The traffic incidents service...</a>
     *
     * @method traffic
     */
    tomtom.traffic = require('services/trafficIncidents');

    /**
     * <a href="../classes/copyright.html">The copyright service...</a>
     *
     * @method copyright
     */
    tomtom.copyright = require('services/copyright');

    /**
     * <a href="../classes/copyrightCaption.html">The copyright caption service...</a>
     *
     * @method copyrightCaption
     */
    tomtom.copyrightCaption = require('services/copyrightCaption');

    /**
     * <a href="../classes/geocode.html">The geocode service...</a>
     *
     * @method geocode
     */
    tomtom.geocode = require('services/geocode');

    /**
     * <a href="../classes/structuredGeocode.html">The structured geocode service...</a>
     *
     * @method structuredGeocode
     */
    tomtom.structuredGeocode = require('services/structuredGeocode');

    /**
     * <a href="../classes/reverseGeocode.html">The reverse geocode service...</a>
     *
     * @method reverseGeocode
     */
    tomtom.reverseGeocode = require('services/reverseGeocode/reverseGeocode');
    /**
     * <a href="../classes/crossStreetLookup.html">The cross street lookup service...</a>
     *
     * @method crossStreetLookup
     */
    tomtom.crossStreetLookup = require('services/reverseGeocode/crossStreetLookup');

    /**
     * <a href="../classes/fuzzySearch.html">The fuzzy search service...</a>
     *
     * @method fuzzySearch
     */
    tomtom.fuzzySearch = require('services/search/fuzzySearch');
    /**
     * <a href="../classes/poiSearch.html">The poi search service...</a>
     *
     * @method poiSearch
     */
    tomtom.poiSearch = require('services/search/poiSearch');
    /**
     * <a href="../classes/categorySearch.html">The category search service...</a>
     *
     * @method categorySearch
     */
    tomtom.categorySearch = require('services/search/categorySearch');
    /**
     * <a href="../classes/geometrySearch.html">The geometry search service...</a>
     *
     * @method geometrySearch
     */
    tomtom.geometrySearch = require('services/search/geometrySearch');
    /**
     * <a href="../classes/nearbySearch.html">The nearby search service...</a>
     *
     * @method nearbySearch
     */
    tomtom.nearbySearch = require('services/search/nearbySearch');
    /**
     * <a href="../classes/lowBandwidthSearch.html">The low bandwidth search service...</a>
     *
     * @method lowBandwidthSearch
     */
    tomtom.lowBandwidthSearch = require('services/search/lowBandwidthSearch');

    /**
     * <a href="../classes/routing.html">The routing service...</a>
     *
     * @method routing
     */
    tomtom.routing = require('services/routing');

    /**
     * <a href="./searchBox#methods_searchBox">The search box widget...</a>
     *
     * @method searchBox
     */
    tomtom.searchBox = require('widgets/controls/searchBox');
    /**
     * <a href="../classes/searchBox.html">The location widget...</a>
     *
     * @method location
     */
    tomtom.location = require('widgets/controls/location');
    /**
     * <a href="../classes/routeInputs.html">The route inputs widget...</a>
     *
     * @method routeInputs
     */
    tomtom.routeInputs = require('widgets/controls/routeInputs');
    /**
     * <a href="../classes/routeInstructions.html">The route instructions widget...</a>
     *
     * @method routeInstructions
     */
    tomtom.routeInstructions = require('widgets/controls/routeInstructions');
    /**
     * <a href="../classes/messageBox.html">The messageBox widget...</a>
     *
     * @method messageBox
     */
    tomtom.messageBox = L.messageBox;
    /**
     * <a href="../classes/unitFormatConverter.html">The unitFormatConverter util...</a>
     *
     * @method unitFormatConverter
     */
    tomtom.unitFormatConverter = require('unitFormatConverter/unitFormatConverter');

    /**
     * <a href="../classes/markersView.html">The MarkersView allows to display search services results on a map..</a>
     *
     * @method markersView
     */
    tomtom.markersView = require('widgets/views/markers');

    /**
     * <a href="../classes/routeOnMap.html">The RouteOnMap view allows to display a route on map...</a>
     *
     * @method routeOnMap
     */
    tomtom.routeOnMap = require('widgets/views/routeOnMap');

    /**
     * <a href="../classes/routeSummary.html">The RouteSummary view allow to display a additional route information...</a>
     *
     * @method routeSummary
     */
    tomtom.routeSummary = require('widgets/views/routeSummary');

    /**
     * <a href="../classes/minimap.html">The MiniMap plugin...</a>
     *
     * @method miniMap
     */
    tomtom.miniMap = require('widgets/views/miniMap');

    security = require('security');
    /**
     * Sets a key 'config' object in the security configuration file. When the key's name is omitted, then the default key is set.
     *
     * @example
     * ```javascript
     *
     * tomtom.key('keyValue');  // sets global key
     * tomtom.key('service/traffic', 'keyValue'); // sets key for traffic service
     * ```
     *
     * @method key
     * @param {String} [name] Name of the key to be set.
     * @param {String} value Value of the key.
     */
    tomtom.key = security.setKey;
    /**
     * Sets a key for the services: map tiles and copyrights. Can also be passed on the map object's initialization with
     * _key_ parameter.
     *
     * @example
     * ```javascript
     *
     * tomtom.mapsKey('keyValue');  // sets maps key
     * ```
     *
     * @method mapsKey
     * @param {String} value Value of the maps key.
     */
    tomtom.mapsKey = security.setKeyForName('services/display');
    /**
     * Sets a key for the vector tiles service. Can also be passed on the map object's initialization
     * with the _key_ parameter or using _key_ method. In cases where default key covers vectors tile service as well
     * setting specific key here is not required.
     *
     * @example
     * ```javascript
     *
     * tomtom.vectorMapsKey('keyValue');  // sets the vector maps key
     * ```
     *
     * @method vectorMapsKey
     * @param {String} value Value of the vector maps key.
     */
    tomtom.vectorMapsKey = security.setKeyForName('services/display/vector');
    /**
     * Sets a key for the traffic incidents tiles and services. Can also be passed on the map object's
     * initialization as an option of the traffic incidents layer.
     *
     * @example
     * ```javascript
     *
     * tomtom.trafficKey('keyValue');  // sets traffic key
     * ```
     *
     * @method trafficKey
     * @param {String} value Value of the traffic key.
     */
    tomtom.trafficKey = security.setKeyForName('services/traffic');
    /**
     * Sets a key for traffic flow tiles service. Can also be passed on the map object's initialization as an option of the traffic
     * flow layer.
     *
     * @example
     * ```javascript
     *
     * tomtom.trafficFlowKey('keyValue');  // sets traffic flow key
     * ```
     *
     * @method trafficFlowKey
     * @param {String} value Value of the traffic flow key.
     */
    tomtom.trafficFlowKey = security.setKeyForName('services/trafficFlow');
    /**
     * Sets a key for search, geocode and reverse geocode services.
     *
     * @example
     * ```javascript
     *
     * tomtom.searchKey('keyValue');  // sets search key
     * ```
     *
     * @method searchKey
     * @param {String} value Value of the search key.
     */
    tomtom.searchKey = security.setKeyForName('services/search');
    /**
     * Sets a key for the routing service.
     *
     * @example
     * ```javascript
     *
     * tomtom.routingKey('keyValue');  // sets routing key
     * ```
     *
     * @method routingKey
     * @param {String} value Value of the routing key.
     */
    tomtom.routingKey = security.setKeyForName('services/routing');

    mapboxProvider = require('mapboxProvider');
    /**
     * Sets Mapbox GL JS. Required if you use Maps SDK version without Mapbox GL JS embedded. Currently supported
     * version of Mapbox GL JS: v0.29.0
     *
     * @example
     * ```javascirpt
     *
     * tomtom.setMapbox(mapboxgl); //sets mapboxgl reference
     * ```
     *
     * @method setMapbox
     * @param {Object} Object Reference to mapboxgl object (global object provided by Mapbox GL JS library).
     */
    tomtom.setMapbox = mapboxProvider.setMapbox;
    return tomtom;
});

